import React, { useState } from 'react';
import config from '../../config/config';
import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  Image,
  Stack,
  Text,
  VStack,
  HStack,
  Badge,
  useColorModeValue,
  Icon,

  Progress,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
} from '@chakra-ui/react';
import { FiCheck, FiFile } from 'react-icons/fi';
import { useNominationForm } from '../../context/NominationFormContext';
import batchFileUploadService from '../../services/batchFileUploadService';
import FileUploadErrorBoundary, { useFileUploadErrorHandler } from '../common/FileUploadErrorBoundary';
import { validateFiles } from '../../utils/fileValidation';
import logger from '../../utils/logger';
import { useToast } from '@chakra-ui/react';

/**
 * ReviewForm component
 * Final step of the application form for reviewing all entered information
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - All form data from previous steps
 * @param {Function} props.onSubmit - Function to call when form is submitted
 * @param {boolean} props.isSubmitting - Whether the form is currently submitting
 */
const ReviewForm = ({ formData, onSubmit, isSubmitting }) => {
  const [isUploadingFiles, setIsUploadingFiles] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const { state, dispatch } = useNominationForm();
  const toast = useToast();
  
  // Create logger instance for this component
  const reviewLogger = logger.create('ReviewForm');
  
  // Error handler for file upload errors
  const handleFileUploadError = useFileUploadErrorHandler((errorInfo) => {
    setUploadError(errorInfo.userMessage);
    setIsUploadingFiles(false);
    toast({
      title: 'خطأ في رفع الملفات',
      description: errorInfo.userMessage,
      status: 'error',
      duration: 8000,
      isClosable: true,
    });
  });
  // Get photo preview URL
  const getPhotoUrl = (photo) => {
    if (photo?.file instanceof File) {
      return URL.createObjectURL(photo.file);
    } else if (photo?.url) {
      return photo.url;
    } else if (typeof photo === 'string') {
      return photo;
    }
    return '';
  };

  // Get file name from file object or string
  const getFileName = (file) => {
    if (file?.file instanceof File) {
      return file.file.name;
    } else if (file?.name) {
      return file.name;
    } else if (typeof file === 'string') {
      // Extract file name from URL or path
      const parts = file.split('/');
      return parts[parts.length - 1];
    }
    return 'ملف';
  };
  
  // Get branch name from branch ID
  const getBranchName = (branchId) => {
    const branch = config.organization.branches.find(b => b.id === branchId);
    return branch ? branch.name : branchId;
  };
  
  // Get center name from center ID
  const getCenterName = (centerId) => {
    const center = config.organization.centers.find(c => c.id === centerId);
    return center ? center.name : centerId;
  };
  
  // Handle file upload progress updates
  const handleUploadProgress = (progress) => {
    setUploadProgress(progress);
    
    // Handle different status updates
    if (progress.status === 'retrying') {
      toast({
        title: `جاري إعادة المحاولة (${progress.attempt}/${progress.maxRetries})`,
        description: `إعادة محاولة رفع ${progress.fileName}`,
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
    }
  };
  
  // Handle form submission with file uploads
  const handleSubmit = async () => {
    try {
      setIsUploadingFiles(true);
      setUploadError(null);
      setUploadProgress(null);
      
      reviewLogger.info('Starting form submission process');
      
      // Check if there are files to upload
      const documents = formData.documents || {};
      const hasFilesToUpload = Object.values(documents).some(doc => doc?.file instanceof File);
      
      let updatedDocuments = { ...documents };
      
      // Get nomination ID from context if available
      const nominationId = state.nominationId;
      
      // Upload files if needed
      if (hasFilesToUpload) {
        reviewLogger.info('Files detected, starting upload process', { 
          fileCount: Object.values(documents).filter(doc => doc?.file instanceof File).length,
          nominationId 
        });
        
        // Validate files before upload
        try {
          const filesToValidate = Object.values(documents)
            .filter(doc => doc?.file instanceof File)
            .map(doc => doc.file);
          
          validateFiles(filesToValidate);
          reviewLogger.info('File validation successful');
        } catch (validationError) {
          reviewLogger.error('File validation failed', { error: validationError.message });
          throw validationError;
        }
        
        toast({
          title: "جاري رفع الملفات",
          description: "يرجى الانتظار حتى يتم رفع جميع الملفات",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        
        // Upload all files using server-side upload with progress tracking
        updatedDocuments = await batchFileUploadService.uploadFiles(
          documents, 
          nominationId,
          handleUploadProgress
        );
        
        reviewLogger.info('File upload completed successfully', {
          uploadedFileTypes: Object.keys(updatedDocuments)
        });
        
        // Update form data with uploaded file information
        const updatedFormData = {
          ...formData,
          documents: updatedDocuments
        };
        
        // Update the form context
        dispatch({
          type: 'UPDATE_STEP_DATA',
          payload: {
            step: 2,
            data: updatedDocuments,
            isValid: true
          }
        });
        
        toast({
          title: "تم رفع الملفات بنجاح",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        
        // Call the original onSubmit with updated form data
        onSubmit(updatedFormData);
      } else {
        reviewLogger.info('No files to upload, proceeding with direct submission');
        // No files to upload, proceed with submission
        onSubmit(formData);
      }
    } catch (error) {
      reviewLogger.error('Error during form submission', {
        error: error.message,
        stack: error.stack,
        originalError: error.originalError?.message
      });
      
      // Use the error handler for consistent error handling
      handleFileUploadError(error, { context: 'form_submission' });
    } finally {
      setIsUploadingFiles(false);
    }
  };

  return (
    <FileUploadErrorBoundary
      userFriendlyMessage="حدث خطأ أثناء تحميل نموذج المراجعة. يرجى إعادة تحميل الصفحة."
      onRetry={() => window.location.reload()}
    >
      <Box>
      <Stack spacing={8}>
        <Box textAlign="center">
          <Heading as="h2" size="md" mb={4}>
            مراجعة الطلب
          </Heading>
          <Text color="gray.600" mb={6}>
            يرجى مراجعة جميع المعلومات التي قمت بإدخالها قبل تقديم الطلب
          </Text>
        </Box>

        {/* Personal Information */}
        <Box
          p={6}
          borderWidth="1px"
          borderRadius="lg"
          borderColor="gray.200"
          bg={useColorModeValue('white', 'gray.700')}
        >
          <Flex justify="space-between" align="center" mb={4}>
            <Heading as="h3" size="sm">
              المعلومات الشخصية
            </Heading>
            <Badge colorScheme="green" p={2} borderRadius="md">
              <HStack>
                <Icon as={FiCheck} />
                <Text>مكتمل</Text>
              </HStack>
            </Badge>
          </Flex>
          <Divider mb={4} />

          <Grid templateColumns={{ base: '1fr', md: '1fr 3fr' }} gap={6}>
            {/* Personal Details */}
            <GridItem colSpan={{ base: 1, md: 2 }}>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                <GridItem>
                  <Text fontWeight="bold">الاسم الكامل</Text>
                  <Text>
                    {formData.personalInfo?.firstName} {formData.personalInfo?.lastName}
                  </Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold">اسم الأب</Text>
                  <Text>{formData.personalInfo?.fatherName}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold">البريد الإلكتروني</Text>
                  <Text>{formData.personalInfo?.email}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold">رقم الهاتف</Text>
                  <Text>{formData.personalInfo?.phone}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold">الوظيفة</Text>
                  <Text>{formData.personalInfo?.jobTitle}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold">جهة العمل</Text>
                  <Text>{formData.personalInfo?.employer}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold">رقم الهوية الوطنية</Text>
                  <Text>{formData.personalInfo?.nationalId}</Text>
                </GridItem>
                <GridItem>
                  <Text fontWeight="bold">عضوية 2025</Text>
                  <Text>
                    {formData.personalInfo?.membershipStatus === 'new' ? 'عضو جديد' : 'تجديد عضوية'}
                  </Text>
                </GridItem>
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <Text fontWeight="bold">العنوان الوطني</Text>
                  <Text>
                    {formData.personalInfo?.nationalAddress}, {formData.personalInfo?.city}, 
                    {formData.personalInfo?.district}, {formData.personalInfo?.street}, 
                    {formData.personalInfo?.buildingNumber}, {formData.personalInfo?.postalCode}
                  </Text>
                </GridItem>
              </Grid>
            </GridItem>
          </Grid>
        </Box>

        {/* Documents */}
        <Box
          p={6}
          borderWidth="1px"
          borderRadius="lg"
          borderColor="gray.200"
          bg={useColorModeValue('white', 'gray.700')}
        >
          <Flex justify="space-between" align="center" mb={4}>
            <Heading as="h3" size="sm">
              المستندات
            </Heading>
            <Badge colorScheme="green" p={2} borderRadius="md">
              <HStack>
                <Icon as={FiCheck} />
                <Text>مكتمل</Text>
              </HStack>
            </Badge>
          </Flex>
          <Divider mb={4} />

          <VStack align="stretch" spacing={4}>
            {/* Personal Photo */}
            {(formData.documents?.personalPhoto || formData.documents?.personal_photo) && (
              <Box>
                <Text fontWeight="bold">الصورة الشخصية</Text>
                <Flex mt={2}>
                  <Box
                    borderWidth="1px"
                    borderRadius="md"
                    overflow="hidden"
                    width="150px"
                    height="150px"
                  >
                    <Image
                      src={getPhotoUrl(formData.documents.personalPhoto || formData.documents.personal_photo)}
                      alt="صورة المرشح"
                      objectFit="cover"
                      width="100%"
                      height="100%"
                    />
                  </Box>
                </Flex>
              </Box>
            )}

            {/* Security Clearance */}
            {(formData.documents?.securityClearance || formData.documents?.security_clearance) && (
              <Box>
                <Text fontWeight="bold">المسح الأمني</Text>
                <Badge
                  mt={2}
                  p={2}
                  borderRadius="md"
                  colorScheme="blue"
                  variant="outline"
                >
                  <HStack>
                    <Icon as={FiFile} />
                    <Text>{getFileName(formData.documents.securityClearance || formData.documents.security_clearance)}</Text>
                  </HStack>
                </Badge>
              </Box>
            )}

            {/* CV */}
            {formData.documents?.cv && (
              <Box>
                <Text fontWeight="bold">السيرة الذاتية</Text>
                <Badge
                  mt={2}
                  p={2}
                  borderRadius="md"
                  colorScheme="blue"
                  variant="outline"
                >
                  <HStack>
                    <Icon as={FiFile} />
                    <Text>{getFileName(formData.documents.cv)}</Text>
                  </HStack>
                </Badge>
              </Box>
            )}
          </VStack>
        </Box>

        {/* Nomination Information */}
        <Box
          p={6}
          borderWidth="1px"
          borderRadius="lg"
          borderColor="gray.200"
          bg={useColorModeValue('white', 'gray.700')}
        >
          <Flex justify="space-between" align="center" mb={4}>
            <Heading as="h3" size="sm">
              معلومات الترشيح
            </Heading>
            <Badge colorScheme="green" p={2} borderRadius="md">
              <HStack>
                <Icon as={FiCheck} />
                <Text>مكتمل</Text>
              </HStack>
            </Badge>
          </Flex>
          <Divider mb={4} />

          <VStack align="stretch" spacing={4}>
            {/* Branch and Center */}
            <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
              <GridItem>
                <Text fontWeight="bold">الفرع</Text>
                <Text>{getBranchName(formData.nomination?.branch)}</Text>
              </GridItem>
              <GridItem>
                <Text fontWeight="bold">المركز</Text>
                <Text>{getCenterName(formData.nomination?.center)}</Text>
              </GridItem>
            </Grid>


            {/* Regulations Acknowledgment */}
            <Box>
              <Text fontWeight="bold">الاطلاع على اللوائح</Text>
              <Text color="green.500">
                <Icon as={FiCheck} mr={1} />
                {formData.nomination?.readRegulations ? 'تم الاطلاع على لوائح المجلس' : 'لم يتم الاطلاع على اللوائح'}
              </Text>
            </Box>

            {/* Responsibilities Confirmation */}
            <Box>
              <Text fontWeight="bold">تأكيد المسؤوليات</Text>
              <Text color="green.500">
                <Icon as={FiCheck} mr={1} />
                {formData.nomination?.confirmResponsibilities ? 'تم تأكيد الالتزام بالمسؤوليات' : 'لم يتم تأكيد الالتزام بالمسؤوليات'}
              </Text>
            </Box>

            {/* Association Knowledge */}
            <Box>
              <Text fontWeight="bold">المعرفة بالجمعية</Text>
              <Text>{formData.nomination?.associationKnowledge}</Text>
            </Box>

            {/* Social Responsibility */}
            <Box>
              <Text fontWeight="bold">المسؤولية الاجتماعية</Text>
              <Text>{formData.nomination?.socialResponsibility}</Text>
            </Box>

            {/* Vision 2030 Programs */}
            <Box>
              <Text fontWeight="bold">برامج رؤية 2030</Text>
              <Text>{formData.nomination?.vision2030Programs}</Text>
            </Box>

            {/* Center Income Ideas */}
            <Box>
              <Text fontWeight="bold">أفكار لزيادة دخل المركز</Text>
              <Text>{formData.nomination?.centerIncomeIdeas}</Text>
            </Box>

            {/* Terms and Conditions */}
            <Box>
              <Text fontWeight="bold">الشروط والأحكام</Text>
              <Text color="green.500">
                <Icon as={FiCheck} mr={1} />
                {formData.nomination?.acceptTerms ? 'تم الإقرار و الموافقة على الشروط والأحكام' : 'لم يتم الإقرار و الموافقة على الشروط والأحكام'}
              </Text>
              {formData.nomination?.acceptTerms && (
                <Text fontSize="sm" mt={1} color="gray.600">
                  • من ذوي السمعة الحسنة<br />
                  • غير محكوم علي بإدانة في جريمة مخلة بالشرف أو الأمانة مالم يكن قد رد إلي اعتباري<br />
                  • كامل الأهلية المعتبرة شرعاً
                </Text>
              )}
            </Box>
          </VStack>
        </Box>

        {/* Upload Progress Display */}
        {uploadProgress && (
          <Box mt={4} p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200">
            <Heading as="h4" size="sm" mb={2}>تقدم رفع الملفات</Heading>
            
            <Progress 
              value={uploadProgress.overallProgress || 0} 
              size="md" 
              colorScheme="primary" 
              mb={2} 
              borderRadius="md"
            />
            
            <Text fontSize="sm" textAlign="center">
              {uploadProgress.status === 'completed' ? 
                'اكتمل رفع جميع الملفات' : 
                `جاري رفع الملفات (${uploadProgress.completedFiles || 0}/${uploadProgress.totalFiles || 0})`}
            </Text>
            
            {uploadProgress.fileName && (
              <Text fontSize="xs" mt={1} textAlign="center">
                {uploadProgress.status === 'retrying' ? 
                  `جاري إعادة محاولة رفع: ${uploadProgress.fileName} (محاولة ${uploadProgress.attempt}/${uploadProgress.maxRetries})` : 
                  `الملف الحالي: ${uploadProgress.fileName}`}
              </Text>
            )}
          </Box>
        )}
        
        {/* Upload Error Display */}
        {uploadError && (
          <Alert status="error" mt={4} borderRadius="md">
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>خطأ في رفع الملفات</AlertTitle>
              <AlertDescription display="block">{uploadError}</AlertDescription>
            </Box>
            <CloseButton 
              position="absolute" 
              right="8px" 
              top="8px" 
              onClick={() => setUploadError(null)} 
            />
          </Alert>
        )}

        <Box mt={8} textAlign="center">
          <Button
            colorScheme="primary"
            size="lg"
            onClick={handleSubmit}
            isLoading={isSubmitting || isUploadingFiles}
            loadingText={isUploadingFiles ? "جاري رفع الملفات..." : "جاري الإرسال..."}
            disabled={isSubmitting || isUploadingFiles}
          >
            تقديم الطلب
          </Button>
          <Text mt={4} fontSize="sm" color="gray.500">
            بالضغط على زر "تقديم الطلب" أنت توافق على أن جميع المعلومات المقدمة صحيحة ودقيقة
          </Text>
        </Box>
      </Stack>
    </Box>
    </FileUploadErrorBoundary>
  );
};

export default ReviewForm;
